import type { ViewTechnician } from '../../api/viewTechnicianApiService';
import { useViewTechnicianContent } from '../../hooks/useViewTechnicianContent';

interface ViewTechnicianContentProps {
  technician: ViewTechnician | null;
}

const ViewTechnicianContent = ({ technician }: ViewTechnicianContentProps) => {
  const {
    formattedTechnicianId,
    formattedEmail,
    formattedPhoneNumber,
    formattedRole,
    formattedAddressLine1,
    formattedAddressLine2,
    formattedCity,
    formattedState,
    formattedZipCode,
    formattedCountry,
  } = useViewTechnicianContent(technician);

  return (
    <div className="flex flex-col gap-6 m-[6px]">
      <div className="flex flex-col gap-4 border border-[#DCDDE0] rounded-[8px] p-4">
        <p
          className="text-[18px] font-medium text-on-surface-variant"
          aria-label="Podstawowe dane"
          data-testid="view-technician-content-title"
        >
          Podstawowe dane
        </p>
        <div
          className="grid gap-x-8 gap-y-4"
          style={{
            gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
          }}
        >
          <div className="flex min-w-0 flex-col gap-2">
            <h2
              className="text-bodySmall font-normal text-on-surface-variant"
              data-testid="ID technika"
            >
              ID technika
            </h2>
            <p
              className="min-w-0 wrap-break-word text-bodyLarge font-normal text-neutral-10 align-middle"
              data-testid="ID technika value"
            >
              {formattedTechnicianId}
            </p>
          </div>
          <div className="flex min-w-0 flex-col gap-2">
            <h2
              className="text-bodySmall font-normal text-on-surface-variant"
              data-testid="Adres e-mail"
            >
              Adres e-mail
            </h2>
            <p
              className="min-w-0 wrap-break-word text-bodyLarge font-normal text-neutral-10 align-middle"
              data-testid="Adres e-mail value"
            >
              {formattedEmail}
            </p>
          </div>
          <div className="flex min-w-0 flex-col gap-2">
            <h2
              className="text-bodySmall font-normal text-on-surface-variant"
              data-testid="Numer telefonu"
            >
              Numer telefonu
            </h2>
            <p
              className="min-w-0 wrap-break-word text-bodyLarge font-normal text-neutral-10 align-middle"
              data-testid="Numer telefonu value"
            >
              {formattedPhoneNumber}
            </p>
          </div>
          <div className="flex min-w-0 flex-col gap-2">
            <h2
              className="text-bodySmall font-normal text-on-surface-variant"
              data-testid="Rola"
            >
              Rola
            </h2>
            <p
              className="min-w-0 wrap-break-word text-bodyLarge font-normal text-neutral-10 align-middle"
              data-testid="Rola value"
            >
              {formattedRole}
            </p>
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-4 border border-[#DCDDE0] rounded-[8px] p-4">
        <p
          className="text-[18px] font-medium text-on-surface-variant"
          aria-label="Dane adresowe"
          data-testid="view-technician-address-title"
        >
          Dane adresowe
        </p>
        <div
          className="grid gap-x-8 gap-y-8"
          style={{
            gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
          }}
        >
          <div className="flex min-w-0 flex-col gap-2">
            <h2
              className="text-bodySmall font-normal text-on-surface-variant"
              data-testid="Adres (linia 1)"
            >
              Adres (linia 1)
            </h2>
            <p
              className="min-w-0 wrap-break-word text-bodyLarge font-normal text-neutral-10 align-middle"
              data-testid="Adres (linia 1) value"
            >
              {formattedAddressLine1}
            </p>
          </div>
          <div className="flex min-w-0 flex-col gap-2">
            <h2
              className="text-bodySmall font-normal text-on-surface-variant"
              data-testid="Adres (linia 2)"
            >
              Adres (linia 2)
            </h2>
            <p
              className="min-w-0 wrap-break-word text-bodyLarge font-normal text-neutral-10 align-middle"
              data-testid="Adres (linia 2) value"
            >
              {formattedAddressLine2}
            </p>
          </div>
          <div className="flex min-w-0 flex-col gap-2">
            <h2
              className="text-bodySmall font-normal text-on-surface-variant"
              data-testid="Miejscowość"
            >
              Miejscowość
            </h2>
            <p
              className="min-w-0 wrap-break-word text-bodyLarge font-normal text-neutral-10 align-middle"
              data-testid="Miejscowość value"
            >
              {formattedCity}
            </p>
          </div>
          <div className="flex min-w-0 flex-col gap-2">
            <h2
              className="text-bodySmall font-normal text-on-surface-variant"
              data-testid="Województwo"
            >
              Województwo
            </h2>
            <p
              className="min-w-0 wrap-break-word text-bodyLarge font-normal text-neutral-10 align-middle"
              data-testid="Województwo value"
            >
              {formattedState}
            </p>
          </div>
          <div className="flex min-w-0 flex-col gap-2">
            <h2
              className="text-bodySmall font-normal text-on-surface-variant"
              data-testid="Kod pocztowy"
            >
              Kod pocztowy
            </h2>
            <p
              className="min-w-0 wrap-break-word text-bodyLarge font-normal text-neutral-10 align-middle"
              data-testid="Kod pocztowy value"
            >
              {formattedZipCode}
            </p>
          </div>
          <div className="flex min-w-0 flex-col gap-2">
            <h2
              className="text-bodySmall font-normal text-on-surface-variant"
              data-testid="Kraj"
            >
              Kraj
            </h2>
            <p
              className="min-w-0 wrap-break-word text-bodyLarge font-normal text-neutral-10 align-middle"
              data-testid="Kraj value"
            >
              {formattedCountry}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewTechnicianContent;
